import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    IconButton,
    InputAdornment,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { SearchIcon } from 'src/icons/SearchIcon';
import { ChevronDownIcon } from 'src/icons/ChevronDownIcon';
import { BaseDialog } from 'src/components/BaseDialog/BaseDialog';
import { NestedAccordionView } from 'src/components/NestedAccordionView/NestedAccordionView';
import { DeviceCategory, DeviceItem, DeviceSubcategory, MyProduct } from 'src/model/api/v2/data-contracts';
import { filterDevices, flattenDevices, reconstructDeviceTree } from 'src/pages/PlannerPage/utils/device-tree-utils';
import { PlaceableDevice, mapDeviceItemToPlaceable, mapMyProductToPlaceable } from 'src/pages/PlannerPage/Planner/dialogs/ReplaceDeviceDialog/placeable-device';
import { calcImageURL } from 'src/utils/url';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';
import TintedSVG from 'src/pages/PlannerPage/DeviceView/TintedSVG';
import {
    checkGroupingCompatibility,
    executeChange,
    IncompatibilityReason,
    ReplacementScope,
} from 'src/pages/PlannerPage/Planner/dialogs/ReplaceDeviceDialog/replaceDeviceActions';
import { getSourceDeviceInfo, SourceDeviceInfo } from 'src/pages/PlannerPage/utils/device-utils';

interface ReplaceDeviceDialogProps {
    onClose: () => void;
    deviceId: number;
    categories: DeviceCategory[];
    myProducts: MyProduct[];
}

export function ReplaceDeviceDialog({ onClose, deviceId, categories, myProducts }: ReplaceDeviceDialogProps) {
    const [searchText, setSearchText] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<PlaceableDevice | null>(null);
    const [scope, setScope] = useState<ReplacementScope>(ReplacementScope.SINGLE);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const sourceInfo: SourceDeviceInfo = useMemo(() => getSourceDeviceInfo(deviceId), [deviceId]);

    const filteredCategories: DeviceCategory[] = useMemo(() => {
        if (!categories.length) return categories;
        const allDevices: DeviceItem[] = flattenDevices(categories);
        const allDevicesWithoutSourceDevice: DeviceItem[] = sourceInfo.isCustom
            ? allDevices
            : allDevices.filter(d => d.id !== sourceInfo.device.product_name_id.toString());
        const filtered: DeviceItem[] = filterDevices(searchText, allDevicesWithoutSourceDevice);
        return reconstructDeviceTree(categories, filtered);
    }, [categories, sourceInfo, searchText]);

    const filteredMyProducts: MyProduct[] = useMemo(() => {
        let filtered = myProducts;
        if (sourceInfo.isCustom) {
            filtered = filtered.filter(p => `user_custom_item_${p.userCustomItem.id}` !== sourceInfo.productId);
        }
        if (searchText === '') return filtered;
        return filtered.filter(p =>
            p.userCustomItem.product_name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [myProducts, sourceInfo, searchText]);

    const groupingCheck = useMemo(() => {
        if (!selectedProduct) return { allowed: true };
        return checkGroupingCompatibility(sourceInfo.device, selectedProduct);
    }, [sourceInfo, selectedProduct]);

    const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    }, []);

    const handleSelectProduct = useCallback((device: DeviceItem) => {
        setSelectedProduct(mapDeviceItemToPlaceable(device));
    }, []);

    const handleSelectMyProduct = useCallback((product: MyProduct) => {
        setSelectedProduct(mapMyProductToPlaceable(product));
    }, []);

    const handleConfirm = useCallback(() => {
        if (!selectedProduct) return;

        setIsProcessing(true);
        setProgress(0);

        executeChange(
            deviceId,
            selectedProduct,
            scope,
            (percent) => setProgress(percent),
            (success) => {
                setIsProcessing(false);
                if (success) {
                    onClose();
                }
            }
        );
    }, [deviceId, selectedProduct, scope, onClose]);

    return (
        <BaseDialog
            onClose={onClose}
            disableClose={isProcessing}
            title={ts('replace_device_dialog_title', 'Change Device')}
            titleIcon={<SwapHorizIcon />}
            actionsLeft={
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                        {ts('replace_device_scope', 'Replace scope:')}
                    </Typography>
                    <Select
                        value={scope}
                        onChange={(e) => setScope(e.target.value as ReplacementScope)}
                        size="small"
                        sx={{ '& .MuiSelect-select': { whiteSpace: 'normal' } }}
                    >
                        <MenuItem value={ReplacementScope.SINGLE}>
                            {sourceInfo.isInGroup
                                ? ts('replace_device_scope_group', 'This group')
                                : ts('replace_device_scope_single', 'This device')
                            }
                        </MenuItem>
                        <MenuItem value={ReplacementScope.ROOM}>
                            {`${ts('replace_device_scope_room', 'All in room')} "${sourceInfo.roomName}"`}
                        </MenuItem>
                        <MenuItem value={ReplacementScope.FLOOR}>
                            {`${ts('replace_device_scope_floor', 'All on floor')} "${sourceInfo.floorName}"`}
                        </MenuItem>
                    </Select>
                </Stack>
            }
            actions={
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={!selectedProduct || isProcessing}
                >
                    {ts('replace_device_confirm', 'OK')}
                </Button>
            }
        >
            <Stack spacing={2} sx={{ pt: 2 }}>
                {/* Source → Target display */}
                <Stack direction="row" alignItems="center" justifyContent="center" >
                    <Stack alignItems="center" sx={{ flex: 1, minWidth: 160 }}>
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: 'grey.100',
                            }}
                        >
                            <Box
                                component="img"
                                src={calcImageURL(sourceInfo.image)}
                                alt={sourceInfo.productName}
                                sx={{ width: 40, height: 40, objectFit: 'contain' }}
                            />
                        </Stack>
                        <Typography variant="subtitle2" textAlign="center">
                            {sourceInfo.productName}
                        </Typography>
                    </Stack>

                    <ArrowForwardIcon sx={{ color: 'grey.400', fontSize: 32 }} />

                    <Stack alignItems="center" sx={{ flex: 1, minWidth: 160 }}>
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: 'grey.100',
                            }}
                        >
                            {selectedProduct ? (
                                <Box
                                    component="img"
                                    src={calcImageURL(selectedProduct.icon ?? null)}
                                    alt={selectedProduct.text}
                                    sx={{ width: 40, height: 40, objectFit: 'contain' }}
                                />
                            ) : (
                                <QuestionMarkIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                            )}
                        </Stack>
                        <Typography variant="subtitle2" textAlign="center">
                            {selectedProduct?.text ?? ts('replace_device_select_target', 'Select device')}
                        </Typography>
                    </Stack>
                </Stack>

                {/* Grouping compatibility warning */}
                {selectedProduct && !groupingCheck.allowed && groupingCheck.reason && (
                    <Alert severity="warning">
                        {ts('replace_device_grouping_warning', 'Warning: device grouping will be removed after replacement.')}
                        {' '}
                        {groupingCheck.reason === IncompatibilityReason.SHADING_TYPE_MISMATCH &&
                            ts('replace_device_reason_shading', 'Reason: shading type mismatch.')}
                        {groupingCheck.reason === IncompatibilityReason.LIGHTING_SWITCH_MISMATCH &&
                            ts('replace_device_reason_lighting', 'Reason: lighting switch mismatch.')}
                        {groupingCheck.reason === IncompatibilityReason.GROUPING_INCOMPATIBLE &&
                            ts('replace_device_reason_grouping', 'Reason: target device is not groupable.')}
                    </Alert>
                )}

                {/* Search */}
                <TextField
                    placeholder={ts('replace_device_search', 'Search for devices...')}
                    variant="outlined"
                    size="small"
                    value={searchText}
                    onChange={handleSearchChange}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: searchText && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchText('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {/* Device tree + My Products */}
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {filteredCategories.length === 0 && filteredMyProducts.length === 0 ? (
                        <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                            {ts('replace_device_no_results', 'No devices found.')}
                        </Typography>
                    ) : (
                        <>
                            <NestedAccordionView<DeviceCategory, DeviceSubcategory, DeviceItem>
                                data={filteredCategories}
                                getMainLabel={(category) => category.name}
                                getSubList={(category) => category.subcategories}
                                getSubLabel={(subcategory) => subcategory.name}
                                getItems={(subcategory) => subcategory.devices}
                                getItemKey={(device) => `product_${device.id}`}
                                getItemLabel={(device) => device.text}
                                renderItemIcon={(device) => calcImageURL(device.icon ?? null)}
                                onItemClick={handleSelectProduct}
                                selectedItemKey={selectedProduct?.id}
                                mainLabelColor={theme.palette.primary.main}
                                mainExpandIconColor={theme.palette.primary.main}
                            />

                            {filteredMyProducts.length > 0 && (
                                <Accordion
                                    disableGutters
                                    elevation={0}
                                    square
                                    sx={{
                                        border: 'none',
                                        boxShadow: 'none',
                                        mb: 0,
                                        '&::before': { display: 'none' },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            <ChevronDownIcon
                                                sx={{
                                                    color: theme.palette.primary.main,
                                                    fontSize: 28,
                                                }}
                                            />
                                        }
                                    >
                                        <Typography variant="h6" color={theme.palette.primary.main}>
                                            {ts('my_products', 'My Products')}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <List disablePadding>
                                            {filteredMyProducts.map((product) => (
                                                <ListItem
                                                    key={product.userCustomItem.id}
                                                    onClick={() => handleSelectMyProduct(product)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        backgroundColor:
                                                            selectedProduct?.id === `user_custom_item_${product.userCustomItem.id}`
                                                                ? theme.palette.primary.light
                                                                : 'transparent',
                                                        ':hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                        },
                                                    }}
                                                >
                                                    <ListItemIcon>
                                                        <TintedSVG
                                                            svg={product.icon_svg}
                                                            color={product.icon_color ?? '#000000'}
                                                            size={24}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText primary={product.userCustomItem.product_name} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </>
                    )}
                </Box>

                {/* Progress bar */}
                {isProcessing && (
                    <Box>
                        <LinearProgress variant="determinate" value={progress} />
                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                            {progress}%
                        </Typography>
                    </Box>
                )}
            </Stack>
        </BaseDialog>
    );
}